import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, X } from "lucide-react";
import api from "@/lib/api";

const VERSION_KEY = "vgws_pref_app_version";
const DISMISSED_KEY = "vgws_pref_refresh_dismissed_until";

async function clearBrowserState() {
  try {
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {}
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {}
  try {
    if (typeof indexedDB !== "undefined" && indexedDB.databases) {
      const dbs = await indexedDB.databases();
      await Promise.all(dbs.map((d) => new Promise((res) => {
        const req = indexedDB.deleteDatabase(d.name);
        req.onsuccess = req.onerror = req.onblocked = () => res();
      })));
    }
  } catch {}
  try { sessionStorage.clear(); } catch {}
  try {
    // Preserve only vgws_pref_* keys
    const toKeep = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("vgws_pref_")) toKeep[k] = localStorage.getItem(k);
    }
    localStorage.clear();
    Object.entries(toKeep).forEach(([k, v]) => localStorage.setItem(k, v));
  } catch {}
}

export default function RefreshAppModal({ open, onClose, latestVersion }) {
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const onClear = async () => {
    setBusy(true);
    try {
      await clearBrowserState();
      if (latestVersion) localStorage.setItem(VERSION_KEY, latestVersion);
      toast.success("App cache cleared. Reloading…");
      // hard reload
      setTimeout(() => window.location.reload(true), 400);
    } catch {
      toast.error("Could not clear cache — please try again.");
      setBusy(false);
    }
  };

  const onDismiss = () => {
    const until = Date.now() + 24 * 3600 * 1000;
    try { localStorage.setItem(DISMISSED_KEY, String(until)); } catch {}
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
         data-testid="refresh-modal">
      <div className="w-full max-w-lg bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <RefreshCw size={20} className="text-[#7a1f2b] animate-spin-slow" />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Refresh VGWS Requisition App for the smoothest experience?
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close" data-testid="refresh-modal-x">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm text-[#1f2320]">
          <p className="leading-relaxed">
            We periodically push improvements to VGWS Requisition App. Clearing this device&apos;s cached files and old service-worker data ensures you always see the latest version, avoid stale data, and prevent rare login glitches.
          </p>
          <div>
            <div className="font-serif font-semibold text-[#7a1f2b] mb-1">What this does:</div>
            <ul className="list-disc pl-5 space-y-1 text-[#4a524c]">
              <li>Clears the browser&apos;s site cache for VGWS Requisition App</li>
              <li>Resets the offline service worker (will re-install on next visit)</li>
              <li>Empties old IndexedDB &amp; sessionStorage entries</li>
              <li>Keeps your saved role preference; does not sign you out</li>
            </ul>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onDismiss} className="btn-ghost" data-testid="refresh-not-now">Not now</button>
          <button
            onClick={onClear}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white bg-[#7a1f2b] hover:bg-[#5a1520] border border-[#5a1520] shadow-sm disabled:opacity-50"
            data-testid="refresh-clear-reload"
          >
            <RefreshCw size={16} /> {busy ? "Clearing…" : "Clear & Reload"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Hook helper — call from a top-level component. */
export function useRefreshPrompt() {
  const [open, setOpen] = useState(false);
  const [latest, setLatest] = useState("");

  useEffect(() => {
    api.get("/version").then((r) => {
      const server = r.data?.version;
      if (!server) return;
      setLatest(server);
      const stored = localStorage.getItem(VERSION_KEY);
      const dismissedUntil = Number(localStorage.getItem(DISMISSED_KEY) || 0);
      if (!stored) {
        // first ever — set silently
        localStorage.setItem(VERSION_KEY, server);
      } else if (stored !== server && Date.now() > dismissedUntil) {
        setOpen(true);
      }
    }).catch(() => {});
  }, []);

  return { open, latest, openModal: () => setOpen(true), closeModal: () => setOpen(false) };
}
